import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { parseNoteHTML, generateJSONLD } from "../utils/seoEngine";
import Navbar from "../components/Navbar";
import scale from "../assets/images/scale.webp";
import "../styles/SeoPreview.css";

const SeoPreview = () => {
  const { degree, course, semester, subject } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [displayMessage, setDisplayMessage] = useState("");

  const formattedSubject =
    subject
      ?.replace(/-/g, " ")
      .toUpperCase();
  // Path needs to account for "Semester - N" folder structure
  const [syllabusText, setSyllabusText] = useState("");
  const folderSemester = `Semester - ${semester}`;
  const folderCourse = course === "OU" ? "3YDC" : course;
  const fileName = `/Notes/${folderCourse}/${folderSemester}/${formattedSubject}.html`;
  const canonicalUrl = `https://tejcodex.com/preview/${degree}/${course}/${semester}/${subject}`;



  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!showContent) return;

    const messages = [
      "⚖️ Simple. 😎 Smart. 📖 Law Notes.",
      "📚 Organized by Semester & Subject.",
      "🎓 Prepared for Osmania University.",
      "📝 Study Smarter, Not Harder.",
      "🚀 Success Amplified with TejCodex."
    ];

    let cancelled = false;

    const typeMessage = async (message) => {
      setDisplayMessage("");

      // Type
      for (let i = 0; i < message.length; i++) {
        if (cancelled) return;

        await new Promise((resolve) => {
          setTimeout(resolve, 70);
        });

        if (cancelled) return;

        setDisplayMessage(message.slice(0, i + 1));
      }

      // Hold completed message
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });

      if (cancelled) return;

      // Delete
      for (let i = message.length; i >= 0; i--) {
        if (cancelled) return;

        setDisplayMessage(message.slice(0, i));

        await new Promise((resolve) => {
          setTimeout(resolve, 35);
        });
      }

      if (!cancelled) {
        setMessageIndex((prev) => (prev + 1) % messages.length);
      }
    };

    typeMessage(messages[messageIndex]);

    return () => {
      cancelled = true;
    };
  }, [showContent, messageIndex]);


  useEffect(() => {
    const fetchData = async () => {

      // =========================
      // NOTES DATA
      // =========================
      const parsed = await parseNoteHTML(
        folderCourse,
        formattedSubject,
        degree,
        course,
        semester,
        canonicalUrl
      );

      if (parsed) {

        setData(parsed);

        setSyllabusText(
          parsed.syllabusText
        );
      }
      // =========================
      // SYLLABUS FETCHING
      // =========================

      try {
        const syllabusFileName = folderCourse === "3YDC"
          ? "LLB3YDCOUSyllabus.html"
          : `${folderCourse}OUSyllabus.html`;

        const syllabusFile = `/Syllabus/${folderCourse}/${syllabusFileName}`;

        const response = await fetch(syllabusFile);

        const html = await response.text();

        const parser = new DOMParser();

        const doc = parser.parseFromString(
          html,
          "text/html"
        );

        const bodyText = doc.body.innerText;

        // Escape special regex chars
        const escapedSubject = formattedSubject
          .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          .replace(/\s+/g, "[\\s\\W-_]+");

        // Match subject block
        const regex = new RegExp(
          `${escapedSubject}[\\s\\S]{0,2500}`,
          "i"
        );

        const match = bodyText.match(regex);

        if (match) {
          setSyllabusText(match[0]);
        }

      } catch (err) {

        console.error(
          "Syllabus parsing failed",
          err
        );

      }

      setLoading(false);
    };

    fetchData();

  }, [
    fileName,
    degree,
    course,
    semester,
    subject,
    formattedSubject,
    canonicalUrl,
    folderCourse
  ]);


  const handleCtaClick = () => {
    if (window.gtag) {
      window.gtag("event", "conversion", {
        send_to: "G-6GN0JRKBY0",
        event_category: "Engagement",
        event_label: "SEO Preview CTA Click",
        value: subject
      });
    }
  };

  if (loading) return (
    <div className="seo-loader">
      <div className="loader-circle"></div>
      <span>Optimizing Content for Search...</span>
    </div>
  );

  if (!data) return (
    <div className="seo-error">
      <h2>Oops! Content Not Found</h2>
      <p>The requested note preview is currently unavailable.</p>
      <Link to="/" className="error-home-btn">Return Home</Link>
    </div>
  );

  return (
    <div className="seo-page-wrapper">
      <Helmet>
        <title>{`${formattedSubject} Notes for ${course} Sem ${semester} | TejCodex`}</title>
        <meta name="description" content={data.summary} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${formattedSubject} Notes | TejCodex`} />
        <meta property="og:description" content={data.summary} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${formattedSubject} Notes | TejCodex`} />
        <meta name="twitter:description" content={data.summary} />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify(generateJSONLD(data, canonicalUrl))}
      </script>

      <Navbar />

      <div className="S-welcome">🥇 Preview Material...</div>

      <section className="S-hero-section">
        <div className="S-trapezium S-trapezium-black"></div>
        <div className="S-trapezium S-trapezium-white"></div>

        {showContent && (
          <div className="S-content-layer">
            <div className="S-message">
              <h2>⚖️ {formattedSubject}</h2>
              <div className="S-center">
                <div className="S-hero-meta">
                  <p>{course} • Semester {semester}</p>
                  <p className="S-hero-tagline">Academic Notes • Legal Reference</p>
                </div>
                <button
                  className="S-buttonstyle"
                  onClick={() => navigate("/auth", {
                    state: { from: { pathname: degree === "3YDC" ? `/notes/3YDC/OU/${semester}/${subject}` : `/notes/5YDC/${course}/${semester}/${subject}` } }
                  })}
                >
                  Get Full Access
                </button>
              </div>
            </div>

            <div className="S-circle">
              <img src={scale} alt="Scale" className="S-scale-img" />
            </div>
          </div>
        )}

        <div className="S-msg-trapezium">
          <div className="S-msg-trapezium-black"></div>
          <div className="S-msg-trapezium-white">
            <span className="S-Hmsg-icn">
              {displayMessage}
            </span>
          </div>
        </div>
      </section>

      <article className="seo-preview-container">
        <nav aria-label="Breadcrumb" className="seo-breadcrumbs">
          <ol>
            {data.breadcrumbs.map((b, i) => (
              <li key={i}>
                <Link to={b.path}>{b.name}</Link>
                {i < data.breadcrumbs.length - 1 && <span className="separator">/</span>}
              </li>
            ))}
          </ol>
        </nav>

        <section className="S-content-section">
          <div className="S-section-heading">Subject Overview</div>
          <div className="S-card-outline">
            <p className="S-overview-text">{data.summary}</p>
          </div>
        </section>


        <section className="S-cta-section">
          <div className="S-cta-card">
            <h3>Access Complete Examination Notes</h3>
            <p>
              Unlock structured unit-wise answers, important case references,
              previous year questions, and detailed legal explanations.
            </p>
            <Link
              to="/auth"
              state={{ from: { pathname: degree === "3YDC" ? `/notes/3YDC/OU/${semester}/${subject}` : `/notes/5YDC/${course}/${semester}/${subject}` } }}
              className="S-cta-btn"
              onClick={handleCtaClick}
            >
              Continue to Full Material
            </Link>
            <div className="trust-badges">
              <span>✓ Follows Latest Syllabus</span>
              <span>✓ Detailed Notes</span>
              <span>✓ Quick Preparation</span>
            </div>
          </div>
        </section>

        <section className="S-content-section">
          <div className="S-section-heading">Frequently Asked Questions</div>
          <div className="faq-grid">
            {data.faqs.map((faq, i) => (
              <details key={i} className="faq-item">
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="seo-footer">
          <p>Related Subjects:
            <Link to={`/preview/${degree}/${course}/${semester}/legal-language`}> Legal Language</Link>,
            <Link to={`/preview/${degree}/${course}/${semester}/indian-history`}> Indian History</Link>
          </p>
          <p>© 2026 TejCodex. All rights reserved.</p>
        </footer>
      </article>
    </div>
  );
};

export default SeoPreview;
